import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  vendingMachineQuery,
  productCategories as productCategoriesQuery,
} from '../graphql/queries';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import ClassicLoadingSpinner from '../components/LoadingSpinner/ClassicLoadingSpinner';
import { EditItem, EditSection } from '../components/Layout';
import { updateVendingMachineMutation } from '../graphql/mutations';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'reactstrap';
import { FaPlus } from 'react-icons/fa';
import {
  getDatePartFromIsoDateString,
  getTimePartFromIsoDateString,
} from '@/utils/date';
import Checkbox from '../components/Checkbox';

import { getObjectDiff } from '@/utils/objDiff';

interface RawPickupSlot {
  id: string;
  start: string;
  end: string;
  name: string;
  products: any[];
}

interface PickupSlot {
  id: string;
  start: string;
  end: string;
  name: string;
  startAndEndOnSameDay: boolean;
  products: string[];
}

export const VendingMachineView = function VendingMachineView({
  match: {
    params: { vendingMachineId, pickpackId },
  },
}: {
  match: {
    params: { pickpackId: string | null; vendingMachineId: string | null };
  };
}) {
  const [updateVendingMachine] = useMutation(updateVendingMachineMutation);
  const [vendingMachine, setVendingMachine] = useState<any>({});
  const [intialState, setIntialState] = useState<any>({});
  const [products, setProducts] = useState<any[]>([]);
  const { loading } = useQuery(vendingMachineQuery, {
    variables: { id: vendingMachineId },
    onCompleted: (d) => {
      const vendingMachine = {
        ...d.vendingMachine,
        pickup_slots: (d.vendingMachine.pickup_slots || []).map((ps: any) =>
          fromRawPickupSlot(ps),
        ),
      };
      setVendingMachine(vendingMachine);
      setIntialState(d.vendingMachine);
    },
  });

  const { loading: productsLoading } = useQuery(productCategoriesQuery, {
    variables: { id: pickpackId, filter: 'ALL' },
    fetchPolicy: 'network-only',
    onCompleted: (d) =>
      setProducts(
        (d.productCategories || []).flatMap((cat: any) =>
          (cat.products || []).map((p: any) => ({
            label: p.name,
            value: p.id,
          })),
        ),
      ),
  });

  function save() {
    const vendingMachineWithRawPickupSlots = {
      ...vendingMachine,
      pickup_slots: (vendingMachine.pickup_slots || []).map((ps: PickupSlot) =>
        toRawPickupSlot(ps),
      ),
    };
    const diff = getObjectDiff(
      getMutationInput(intialState),
      getMutationInput(vendingMachineWithRawPickupSlots),
    );

    console.log('vnd: ', JSON.stringify(diff));
    if (Object.keys(diff).length > 0) {
      updateVendingMachine({
        variables: {
          id: vendingMachineId,
          payload: diff,
        },
        update: (cache, { data: { updateVendingMachine: vnd } }) => {
          const vendingMachine = {
            ...vnd,
            pickup_slots: (vnd.pickup_slots || []).map((ps: any) =>
              fromRawPickupSlot(ps),
            ),
          };
          setVendingMachine(vendingMachine);
          setIntialState(vnd);
        },
      });
    }
  }

  function getMutationInput(vendingMachine: any) {
    const res = {
      name: vendingMachine.name,
      pickup_slots: (vendingMachine.pickup_slots || []).map((ps: any) => ({
        id: ps.id,
        start: ps.start,
        end: ps.end,
        products: ps.products,
      })),
    };
    console.log('mutation input: ', JSON.stringify(res));
    return res;
  }

  function fromRawPickupSlot(rawPickSlot: RawPickupSlot) {
    const pickupSlot: PickupSlot = {
      id: rawPickSlot.id,
      start: getTimePartFromIsoDateString(rawPickSlot.start),
      end: getTimePartFromIsoDateString(rawPickSlot.end),
      name: rawPickSlot.name,
      startAndEndOnSameDay: true,
      products: rawPickSlot.products.map((p) => p.id),
    };
    if (
      getDatePartFromIsoDateString(rawPickSlot.start) !==
      getDatePartFromIsoDateString(rawPickSlot.end)
    ) {
      pickupSlot.startAndEndOnSameDay = false;
    }
    return pickupSlot;
  }

  function toRawPickupSlot(pickupSlot: PickupSlot) {
    const rawPickSlot: RawPickupSlot = {
      id: pickupSlot.id,
      start: `2022-01-01T${pickupSlot.start}:00.000Z`,
      end: `2022-01-${pickupSlot.startAndEndOnSameDay ? '01' : '02'}T${
        pickupSlot.end
      }:00.000Z`,
      name: pickupSlot.name,
      products: pickupSlot.products,
    };
    return rawPickSlot;
  }

  function updatePickupSlotName(pickupSlot: PickupSlot) {
    pickupSlot.name = `${pickupSlot.start} - ${pickupSlot.end}${
      pickupSlot.startAndEndOnSameDay ? '' : ' (tagesübergreifend)'
    }`;
    return pickupSlot;
  }

  function addPickupSlot() {
    setVendingMachine((oldVndMachine: any) => {
      const pickupSlots = [...oldVndMachine.pickup_slots];
      console.log(
        'ids',
        pickupSlots
          .map((ps) => parseInt(ps.id.substring(ps.id.lastIndexOf('_') + 1)))
          .concat([0]),
      );
      const nextId =
        1 +
        Math.max(
          ...pickupSlots
            .map((ps) => parseInt(ps.id.substring(ps.id.lastIndexOf('_') + 1)))
            .concat([0]),
        );
      console.log('nextId', nextId);
      let pickupSlot: PickupSlot = {
        id: `ps_${nextId}`,
        start: getTimePartFromIsoDateString(new Date(Date.now()).toISOString()),
        end: getTimePartFromIsoDateString(new Date(Date.now()).toISOString()),
        startAndEndOnSameDay: true,
        name: '',
        products: [],
      };
      pickupSlot = updatePickupSlotName(pickupSlot);
      pickupSlots.push(pickupSlot);
      return { ...oldVndMachine, pickup_slots: pickupSlots };
    });
  }

  function renderStartAndEndOnSameDayCell(ps: PickupSlot): React.ReactNode {
    return (
      <Checkbox
        checked={ps.startAndEndOnSameDay}
        onClick={(e) =>
          updatePickupSlot(
            ps.id,
            'startAndEndOnSameDay',
            !ps.startAndEndOnSameDay,
          )
        }
      />
    );
  }

  function renderProductName(cmp: { product: any }): string {
    if (cmp.product) {
      return cmp.product.name || i18n.t('Common:unknown');
    }
    return i18n.t('Common:unknown');
  }

  function renderPreorderedQuantity(cmp: { preordered: any[] }): number {
    if (Array.isArray(cmp.preordered)) {
      return cmp.preordered.reduce(
        (oAmount: number, o: any) => oAmount + o.quantity,
        0,
      );
    }
    return 0;
  }

  function updatePickupSlot(
    pickupSlotId: string,
    attrName: string,
    attrValue: any,
  ) {
    setVendingMachine((oldVndMachine: any) => {
      const pickupSlots = [...oldVndMachine.pickup_slots];
      const index = pickupSlots.findIndex((ps) => ps.id === pickupSlotId);
      if (index !== -1) {
        pickupSlots[index][attrName] = attrValue;
        if (
          attrName === 'start' ||
          attrName === 'end' ||
          attrName === 'startAndEndOnSameDay'
        ) {
          pickupSlots[index] = updatePickupSlotName(pickupSlots[index]);
        }
        console.error(pickupSlots[index]);
        return { ...oldVndMachine, pickup_slots: pickupSlots };
      } else {
        console.log(`Can not find pickup slot ${pickupSlotId}`);
      }
      return oldVndMachine;
    });
  }

  if (loading || productsLoading || vendingMachine.id == null) {
    return <ClassicLoadingSpinner />;
  }

  const pickupSlotHeader = (
    <div className="align-items-center export-buttons flex">
      <Button
        type="button"
        onClick={addPickupSlot}
        className="p-button-success me-2"
        data-pr-tooltip={i18n.t('VendingMachine:addPickupSlot')}
      >
        <FaPlus />
      </Button>
    </div>
  );

  return (
    <EditSection
      title={i18n.t('Common:vendingMachine')}
      isLoading={loading}
      onSave={save}
    >
      <EditItem title={i18n.t('VendingMachine:generalInfo')}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:name')}
          </span>
          <input
            className="form-control"
            type="text"
            onChange={(e) =>
              setVendingMachine({ ...vendingMachine, name: e.target.value })
            }
            value={vendingMachine.name}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('VendingMachine:storeName')}
          </span>
          <input
            className="form-control"
            type="text"
            value={vendingMachine.store.name}
            disabled
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('VendingMachine:createdAt')}
          </span>
          <input
            className="form-control"
            type="text"
            value={new Date(vendingMachine.created_at).toLocaleDateString(
              i18n.language,
              getDateFormatOptions(false),
            )}
            disabled
          />
        </div>
      </EditItem>
      <EditItem title={i18n.t('VendingMachine:pickupSlots')}>
        <DataTable
          header={pickupSlotHeader}
          value={vendingMachine.pickup_slots}
          dataKey="id"
          responsiveLayout="scroll"
          lazy
          loading={loading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '200px' }}
            body={(ps: any) => ps.name}
            header={i18n.t('Common:name')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(ps: any) => renderTimePickerCell(ps, 'start')}
            header={i18n.t('Common:start')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(ps: any) => renderTimePickerCell(ps, 'end')}
            header={i18n.t('Common:end')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(ps: PickupSlot) => renderStartAndEndOnSameDayCell(ps)}
            header={i18n.t('VendingMachine:startEndOnSameDay')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(ps: any) => (
              <MultiSelect
                value={ps.products}
                options={products}
                onChange={(e) =>
                  updatePickupSlot(ps.id, 'products', e.target.value)
                }
                placeholder={i18n.t('VendingMachine:productsPlaceholder')}
              />
            )}
            header={i18n.t('VendingMachine:products')}
          />
        </DataTable>
      </EditItem>
      <EditItem title={i18n.t('VendingMachine:compartments')}>
        <DataTable
          value={vendingMachine.compartments}
          dataKey="compartment_id"
          responsiveLayout="scroll"
          lazy
          loading={loading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '200px' }}
            body={(cmp: any) => renderProductName(cmp)}
            header={i18n.t('VendingMachine:product')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(cmp: any) => cmp.quantity || 0}
            header={i18n.t('VendingMachine:quantity')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(cmp: any) => renderPreorderedQuantity(cmp) || 0}
            header={i18n.t('VendingMachine:preordered')}
          />
        </DataTable>
      </EditItem>
    </EditSection>
  );

  function renderTimePickerCell(ps: any, attrName: string): React.ReactNode {
    return (
      <input
        type={'time'}
        value={ps[attrName]}
        onChange={(e) => updatePickupSlot(ps.id, attrName, e.target.value)}
      />
    );
  }
};
